import { useQuery } from "@tanstack/react-query";
import { Loader2, Route } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { getMapConfig, getTripRoute, type StopView } from "@/api/logistics";

/**
 * Карта маршрута рейса на Яндекс.Картах:
 *  - нумерованные метки точек в порядке рейса;
 *  - линия маршрута: если удалось построить по дорогам через OSRM
 *    (`/api/logistics/trips/{id}/route`) — рисуем дорожную геометрию + реальный
 *    километраж и время; иначе — прямая линия через точки + оценка по прямой.
 *
 * Дорожный роутинг Яндекса (`multiRouter`) платный — не используем; карту
 * (тайлы + метки + линия) рисует бесплатный JS-ключ (`YANDEX_MAPS_API_KEY`,
 * отдаётся через /api/logistics/map-config).
 */

const ROAD_FACTOR = 1.3; // оценка «по прямой» → «по дорогам», когда OSRM недоступен

// Ленивая загрузка Яндекс.Карт JS API (единожды на страницу).
let ymapsPromise: Promise<unknown> | null = null;

function loadYmaps(apikey: string): Promise<unknown> {
  if (ymapsPromise) return ymapsPromise;
  ymapsPromise = new Promise((resolve, reject) => {
    const w = window as unknown as {
      ymaps?: { ready: (cb: () => void) => void; Map?: unknown };
    };
    if (w.ymaps && w.ymaps.Map) {
      w.ymaps.ready(() => resolve(w.ymaps));
      return;
    }
    const script = document.createElement("script");
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${encodeURIComponent(apikey)}&lang=ru_RU`;
    script.async = true;
    script.onload = () => {
      const wl = window as unknown as { ymaps?: { ready: (cb: () => void) => void } };
      if (wl.ymaps) wl.ymaps.ready(() => resolve(wl.ymaps));
      else {
        // Скрипт загрузился, но ymaps нет (битый/просроченный ключ отдал
        // страницу-ошибку). Сбрасываем промис, иначе rejected-промис кэшируется
        // на всю сессию и карта не переподключится без перезагрузки.
        ymapsPromise = null;
        reject(new Error("Яндекс.Карты не загрузились"));
      }
    };
    script.onerror = () => {
      ymapsPromise = null;
      reject(new Error("Не удалось загрузить Яндекс.Карты"));
    };
    document.head.appendChild(script);
  });
  return ymapsPromise;
}

// Экранируем HTML: label/address вводит пользователь, а balloonContent Яндекса
// рендерит строку как СЫРОЙ HTML (это не JSX, авто-экранирования React нет) →
// без экранирования это stored XSS в origin портала.
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

type Coord = [number, number];

interface Placed {
  coord: Coord;
  position: number;
  title: string;
  address: string;
  internal: boolean;
}

function placedStops(stops: StopView[]): { placed: Placed[]; missing: StopView[] } {
  const placed: Placed[] = [];
  const missing: StopView[] = [];
  for (const s of stops) {
    const la = s.lat ? parseFloat(s.lat) : NaN;
    const lo = s.lon ? parseFloat(s.lon) : NaN;
    if (!Number.isNaN(la) && !Number.isNaN(lo)) {
      placed.push({
        coord: [la, lo],
        position: s.position,
        title: s.label || s.address || `Точка ${s.position}`,
        address: s.address || "",
        internal: s.role === "internal",
      });
    } else if (s.address || s.label) {
      missing.push(s);
    }
  }
  return { placed, missing };
}

function haversineKm(a: Coord, b: Coord): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLon = toRad(b[1] - a[1]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function straightKm(coords: Coord[]): number {
  let km = 0;
  for (let i = 1; i < coords.length; i++) km += haversineKm(coords[i - 1]!, coords[i]!);
  return km * ROAD_FACTOR;
}

export function TripMap({
  tripId,
  stops,
}: {
  tripId: string;
  stops: StopView[];
}): React.ReactElement {
  const cfgQ = useQuery({
    queryKey: ["map-config"],
    queryFn: getMapConfig,
    staleTime: 5 * 60_000,
  });
  const mapRef = useRef<HTMLDivElement>(null);
  const [errMsg, setErrMsg] = useState("");

  const { placed, missing } = placedStops(stops);
  const coords = placed.map((p) => p.coord);
  const placedKey = JSON.stringify(placed);
  const apikey = cfgQ.data?.configured ? cfgQ.data.apikey : "";

  // Дорожный маршрут по OSRM (бэкенд). Пересчитывается при смене точек.
  const routeQ = useQuery({
    queryKey: ["trip-route", tripId, placedKey],
    queryFn: () => getTripRoute(tripId),
    enabled: placed.length >= 2,
    staleTime: 60_000,
    retry: false,
  });
  const route = routeQ.data;

  const roadLine =
    route?.ok && route.geometry.length >= 2 ? (route.geometry as Coord[]) : null;
  const lineCoords: Coord[] | null = roadLine ?? (coords.length >= 2 ? coords : null);
  const lineKey = JSON.stringify(lineCoords);

  useEffect(() => {
    if (!apikey || placed.length === 0) return;
    let map: { destroy: () => void } | null = null;
    let cancelled = false;
    setErrMsg("");

    loadYmaps(apikey)
      .then((ymapsRaw) => {
        if (cancelled || !mapRef.current) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ymaps = ymapsRaw as any;
        map = new ymaps.Map(mapRef.current, {
          center: placed[0]!.coord,
          zoom: 10,
          controls: ["zoomControl"],
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const m = map as any;

        if (lineCoords) {
          m.geoObjects.add(
            new ymaps.Polyline(
              lineCoords,
              {},
              { strokeColor: "#2563eb", strokeWidth: 4, strokeOpacity: 0.75 },
            ),
          );
        }

        placed.forEach((p) => {
          m.geoObjects.add(
            new ymaps.Placemark(
              p.coord,
              {
                iconContent: String(p.position),
                hintContent: p.title,
                balloonContent: `<b>${escapeHtml(p.title)}</b><br>${escapeHtml(p.address)}`,
              },
              {
                preset: p.internal
                  ? "islands#darkGreenStretchyIcon"
                  : "islands#blueStretchyIcon",
              },
            ),
          );
        });

        if (m.geoObjects.getLength() > 0 && coords.length >= 2) {
          m.setBounds(m.geoObjects.getBounds(), {
            checkZoomRange: true,
            zoomMargin: 40,
          });
        } else {
          m.setCenter(placed[0]!.coord, 14);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setErrMsg(e instanceof Error ? e.message : "Ошибка карты");
      });

    return () => {
      cancelled = true;
      if (map) map.destroy();
    };
  }, [apikey, placedKey, lineKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (cfgQ.isLoading) {
    return (
      <div className="flex justify-center py-6 text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
      </div>
    );
  }
  if (!cfgQ.data?.configured) {
    return (
      <p className="text-sm text-muted-foreground">
        Карта не настроена — задайте <code>YANDEX_MAPS_API_KEY</code> в окружении.
      </p>
    );
  }
  if (placed.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Нет точек с координатами. Выбирайте адрес из подсказок DaData — тогда точка
        попадёт на карту.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {coords.length >= 2 && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <Route className="size-4 text-muted-foreground" />
          {routeQ.isFetching ? (
            <span className="text-muted-foreground">Считаем маршрут по дорогам…</span>
          ) : route?.ok ? (
            <>
              Маршрут по дорогам:{" "}
              <span className="font-semibold">{route.distance_km} км</span>
              {route.duration_min != null && (
                <span className="text-muted-foreground">· ~{route.duration_min} мин</span>
              )}
            </>
          ) : (
            <>
              <span className="font-semibold">≈ {straightKm(coords).toFixed(1)} км</span>
              <span className="text-xs text-amber-600">
                (оценка по прямой{route?.message ? ` — по дорогам не вышло: ${route.message}` : ""})
              </span>
            </>
          )}
        </div>
      )}
      {missing.length > 0 && (
        <p className="text-xs text-amber-600">
          Без координат (не на карте):{" "}
          {missing.map((s) => s.label || s.address).join(", ")}
        </p>
      )}
      {errMsg && <p className="text-sm text-amber-600">{errMsg}</p>}
      <div ref={mapRef} className="h-96 w-full overflow-hidden rounded-md border" />
    </div>
  );
}
